import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import type { AppContext } from '@/lib/core/types';
import { useLocaleStore } from '@/lib/core/stores';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import { useClientTranslation } from '@/lib/core/i18n';
import { LinkRouteUrl } from '@/lib/core/utils';
import type {
  CheckoutConfigDto,
  CheckoutContentResponseModel,
  CheckoutPropertiesModel,
  PaymentMethodDto,
} from '@/services/platform/api/model';
import {
  CheckoutPaymentMethodTypes,
  getActivePayments,
  getCheckoutConfig,
  isPaymentResumable,
} from '@/services/platform/data/checkout';
import { getPaymentMethods } from '@/services/platform/data/payment-methods';
import { getPage } from '@/services/platform/data/cms';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { ScreenHead } from '@/components/shared/screen/head';
import { ShopCheckoutErrorScreen } from '@/components/shared/shop/checkout/ShopCheckoutErrorScreen';
import { CheckoutForm, type CheckoutOutcome } from './CheckoutForm';
import { PaymentProcessing } from './PaymentProcessing';
import { forgetCheckout } from './session';
import {
  EMPTY_CHECKOUT_FORM_STATE,
  type CheckoutFormState,
  type CheckoutPurchase,
  type CheckoutSelection,
} from './types';

interface CheckoutScreenProps {
  /**
   * The CMS `checkout` page, for the title and the authored payment-method labels/logos. Optional
   * because a context may reach this screen without one (the shop resolves it itself and must not be
   * blocked from taking a payment if that lookup fails) — the method rows then fall back to
   * `FALLBACK_LOGOS` and the title to `App.Checkout.Title`.
   */
  content?: CheckoutContentResponseModel;
  context?: AppContext;
  /** `undefined` means there is nothing to pay for — a deep link or a refresh lost the booking. */
  purchase: CheckoutPurchase | undefined;
  /** Called once, when the payment reaches `Succeeded` — the caller decides what happens next. */
  onSucceeded: (outcome: Extract<CheckoutOutcome, { kind: 'succeeded' }>) => void;
  /**
   * Lets the adapter claim an outcome before the generic phases see it: return `true` to say it has
   * been handled (typically by navigating somewhere better than an error screen), `false` or nothing
   * to fall through. `succeeded` is never offered here — that is `onSucceeded`'s job.
   *
   * This exists because some outcomes only mean something in context: a re-priced basket belongs
   * back on the bag with the new prices, and an already-paid one belongs on its order — neither is
   * a decline, and offering "retry" for either would be wrong.
   */
  onOutcome?: (outcome: Exclude<CheckoutOutcome, { kind: 'succeeded' }>) => boolean | void;
  /** Forwarded to `CheckoutForm` — see its own doc comment. */
  onIntentCreated?: (paymentId: string) => void;
  /**
   * Overrides how this screen leaves — used by callers that embed it inline in a multi-step flow
   * (e.g. the range-rover booking flow's payment step) rather than reaching it via its own route.
   * Without this, "leaving" means a router navigation to `purchase.returnPath`, which is correct for
   * the dedicated `/checkout` route but would remount an embedding flow instead of stepping back
   * within it.
   */
  onBack?: () => void;
  /**
   * Set by callers that render this inside a screen of their own (a booking flow's payment step)
   * rather than as a whole route.
   *
   * Its only job today is to leave `hideNavbar` alone. `useUIStore.hideNavbar` is a plain boolean,
   * not a counter, so this screen's own unmount cleanup (`setHideNavbar(false)`) would un-hide the
   * navbar for the *enclosing* flow, whose effect has no reason to re-run and re-assert it — the
   * navbar then reappears over whatever step comes after payment. An embedding flow already hides
   * it for its whole lifetime, so this screen must not touch it.
   *
   * Deliberately explicit rather than inferred from `onBack`: the two answer different questions,
   * and a caller that only wants to redirect "back" somewhere custom should not silently change
   * who owns the navbar.
   */
  embedded?: boolean;
}

type Phase =
  | { kind: 'loading' }
  | { kind: 'selecting' }
  | { kind: 'processing'; subtitle?: string }
  | { kind: 'failed'; message: string }
  | { kind: 'pending'; message: string }
  | { kind: 'refunded'; message: string }
  | { kind: 'unavailable' };

/**
 * The context-agnostic checkout body: Stripe boot, saved cards, active-payment recovery, the
 * generic outcome phases, and the actual `<CheckoutForm>`. Extracted from what was originally
 * `EventCheckoutScreen` so a second context (Mews bookings) could reuse it without forking the
 * ~90% of this that was never Event-specific to begin with.
 *
 * Each context gets its own thin adapter (`EventCheckoutScreen`, `MewsCheckoutScreen`): resolve
 * that context's own router-state/session shape, build a `CheckoutPurchase`, render this, and
 * decide what a successful payment means for that context (an event fabricates a registration; a
 * Mews booking already has one and just needs to know payment landed).
 *
 * Stripe is booted with the publishable key from `GET /me/checkout/config` and never re-keyed: a
 * client secret only confirms against the account it belongs to, so swapping the key after the
 * customer has typed a card would remount the iframes and lose it.
 */
export function CheckoutScreen({
  content,
  context,
  purchase,
  onSucceeded,
  onOutcome,
  onIntentCreated,
  onBack,
  embedded = false,
}: CheckoutScreenProps) {
  const t = useClientTranslation();
  const navigate = useNavigate();
  const locale = useLocaleStore(state => state.locale);
  const setHideNavbar = useUIStore(state => state.setHideNavbar);

  // The floating nav pill is hidden in the design (node 1716:92995) and would otherwise sit over the
  // sticky "GO TO PAYMENT" bar and swallow its taps. Skipped when embedded — see the prop's doc.
  useEffect(() => {
    if (embedded) return;
    setHideNavbar(true);
    return () => setHideNavbar(false);
  }, [setHideNavbar, embedded]);

  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [config, setConfig] = useState<CheckoutConfigDto | null>(null);
  const [savedCards, setSavedCards] = useState<PaymentMethodDto[]>([]);
  const [selection, setSelection] = useState<CheckoutSelection>();
  const [resumePaymentId, setResumePaymentId] = useState<string>();

  // Held here, not in `CheckoutForm`: selecting MB WAY re-keys the `<Elements>` group below, which
  // unmounts the form. Owning this above that boundary is what stops a method switch from wiping the
  // card, the name and the accepted terms.
  const [formState, setFormState] = useState<CheckoutFormState>(EMPTY_CHECKOUT_FORM_STATE);
  const patchFormState = useCallback((patch: Partial<CheckoutFormState>) => {
    setFormState(previous => ({ ...previous, ...patch }));
  }, []);

  // Embedding flows (sunbed, stay, experience, range-rover) render this as one of their own steps and
  // have no CMS `checkout` page to hand over, so resolve it here from the `checkout` page mapping.
  // A miss is not fatal — the title and method rows fall back to i18n and `FALLBACK_LOGOS`.
  const [fetchedContent, setFetchedContent] = useState<CheckoutContentResponseModel>();
  const checkoutPath = content ? undefined : LinkRouteUrl(context?.settings?.properties?.checkout?.route);
  useEffect(() => {
    if (!checkoutPath) return;

    let isStale = false;
    getPage<CheckoutContentResponseModel>(checkoutPath, locale ?? undefined, false)
      .then(page => {
        if (!isStale) setFetchedContent(page);
      })
      .catch(error => console.warn('CheckoutScreen: could not load the checkout page:', error));

    return () => {
      isStale = true;
    };
  }, [checkoutPath, locale]);

  const properties = (content ?? fetchedContent)?.properties as CheckoutPropertiesModel | undefined;
  const termsHref = LinkRouteUrl(context?.settings?.properties?.accountTermsConditions?.route);

  // A deep link or refresh loses the booking, and this screen cannot reconstruct what was being
  // paid for. Send the customer back to choose again instead of charging for a guess.
  useEffect(() => {
    if (!purchase) (onBack ?? (() => navigate('/', { replace: true })))();
  }, [purchase, navigate, onBack]);

  /** Back goes to the flow this came from, replacing so the checkout entry cannot be re-entered. */
  const goBack = useCallback(() => {
    forgetCheckout();
    if (onBack) {
      onBack();
      return;
    }
    navigate(purchase?.returnPath ?? '/', { replace: true });
  }, [navigate, purchase?.returnPath, onBack]);

  /*
   * Keyed on the two fields this actually reads, not on `purchase` as a whole.
   *
   * An adapter is free to rebuild its `CheckoutPurchase` when anything on it changes — the shop does,
   * because its summary panel gains a DELIVER TO row once the address book resolves. Depending on the
   * object meant that re-running this re-ran `setSelection`, quietly moving the customer back to the
   * saved card if they had already picked another method before that fetch landed. What is being
   * paid for has not changed in that moment, and nothing here needs to happen again.
   */
  const paymentContext = purchase?.context;
  const paymentContextReference = purchase?.contextReference;

  useEffect(() => {
    if (!paymentContext || !paymentContextReference) return;
    let isStale = false;

    (async () => {
      const [checkoutConfig, cards, active] = await Promise.all([
        getCheckoutConfig(paymentContext, paymentContextReference),
        getPaymentMethods(locale),
        getActivePayments(),
      ]);
      if (isStale) return;

      if (!checkoutConfig?.publishableKey) {
        setPhase({ kind: 'unavailable' });
        return;
      }

      setConfig(checkoutConfig);

      // Checkout only ever offers the customer's preferred card — choosing a different default is
      // a profile-screen decision, not a point-of-payment one. Falls back to the first card with an
      // id if none is flagged preferred (defensive: every saved-card list should have exactly one,
      // per setPreferredPaymentMethod's server-side guarantee, but this must not crash if that ever
      // drifts).
      const validCards = (cards ?? []).filter(card => !!card.id);
      const preferredCard = validCards.find(card => card.isPreferred) ?? validCards[0];
      setSavedCards(preferredCard ? [preferredCard] : []);

      // Only one payment can be in flight per purchase, so an existing one for this reference must
      // be resumed rather than replaced — otherwise the customer is locked out by their own attempt.
      // The status is re-checked rather than trusted from the endpoint's name: a payment that has
      // already settled must not send the screen straight back into polling.
      const inFlight = (active ?? []).find(
        payment =>
          payment.context === paymentContext &&
          payment.contextReference === paymentContextReference &&
          isPaymentResumable(payment.status)
      );
      if (inFlight?.paymentId) setResumePaymentId(inFlight.paymentId);

      if (preferredCard?.id) setSelection({ kind: 'savedCard', paymentMethodId: preferredCard.id });

      setPhase({ kind: 'selecting' });
    })();

    return () => {
      isStale = true;
    };
  }, [paymentContext, paymentContextReference, locale]);

  const stripePromise = useMemo(
    () => (config?.publishableKey ? loadStripe(config.publishableKey) : null),
    [config?.publishableKey]
  );

  /**
   * Elements options.
   *
   * MB WAY can only be confirmed through `PaymentElement`, which needs either a client secret or
   * deferred mode — and the intent does not exist until the customer presses pay, so deferred mode
   * it is. The split card Elements cannot share a group with `PaymentElement`, hence the `key` on
   * the provider below: switching to or from MB WAY remounts the group deliberately.
   */
  const isMbWay = selection?.kind === 'mbWay';
  // Same reason as the effect above: only the amount and currency matter here, and rebuilding these
  // options on an unrelated change to `purchase` would re-key the deferred-mode Elements group.
  const amountMajor = purchase?.amountMajor;
  const currencyCode = purchase?.currencyCode;
  const elementsOptions = useMemo(() => {
    if (!isMbWay || amountMajor === undefined) return undefined;
    const totalMinor = Math.round(amountMajor * 100);
    return {
      mode: 'payment' as const,
      amount: totalMinor,
      currency: (currencyCode ?? 'EUR').toLowerCase(),
      paymentMethodTypes: [CheckoutPaymentMethodTypes.MbWay],
      /*
        The iframe is cross-origin and cannot see the app's `@font-face` rules, so naming the font in
        `appearance` without shipping it here left Stripe applying a font it could not load. It then
        measured the element with fallback metrics, the text wrapped onto more lines than it had
        budgeted for, and the iframe came out ~10-20px short of its own content — the clipping.
        Absolute URLs because the iframe resolves relative paths against Stripe's origin, not ours.
      */
      fonts: [
        {
          family: 'PP Neue Montreal',
          src: `url(${window.location.origin}/fonts/pp-neue-montreal/WEB/PPNeueMontreal-Regular.woff2) format('woff2')`,
          weight: '400',
        },
        {
          family: 'PP Neue Montreal',
          src: `url(${window.location.origin}/fonts/pp-neue-montreal/WEB/PPNeueMontreal-Medium.woff2) format('woff2')`,
          weight: '500',
        },
        {
          family: 'PP Neue Montreal',
          src: `url(${window.location.origin}/fonts/pp-neue-montreal/WEB/PPNeueMontreal-SemiBold.woff2) format('woff2')`,
          weight: '600',
        },
      ],
      appearance: {
        variables: {
          fontFamily: '"PP Neue Montreal", sans-serif',
          fontSizeBase: '14px',
          colorText: '#1a1a1a',
          colorDanger: '#971b2f',
          borderRadius: '0px',
          spacingUnit: '4px',
        },
      },
    };
  }, [isMbWay, amountMajor, currencyCode]);

  const handleOutcome = useCallback(
    (outcome: CheckoutOutcome) => {
      if (outcome.kind === 'succeeded') {
        // Paid — the copy must not survive to be picked up by a later checkout, regardless of what
        // the caller does next.
        forgetCheckout();
        onSucceeded(outcome);
        return;
      }

      // Offered to the adapter first: it may have somewhere better to send the customer than an
      // error screen. Anything it declines falls through to the phases below.
      if (onOutcome?.(outcome)) return;

      /*
       * An exhaustive switch, not an if-chain with a fallthrough. This used to treat "not
       * failed/pending/refunded" as success, which meant every kind added to `CheckoutOutcome`
       * silently became a paid purchase — the failure mode being a confirmation screen for money
       * that was never taken. The `never` check below makes the compiler catch the next one.
       */
      switch (outcome.kind) {
        case 'failed':
        case 'stale':
        case 'alreadyPaid':
        case 'eventSoldOut':
        case 'eventDateGone':
          setPhase({ kind: 'failed', message: outcome.message });
          return;
        case 'pending':
          setPhase({ kind: 'pending', message: outcome.message });
          return;
        case 'refunded':
          setPhase({ kind: 'refunded', message: outcome.message });
          return;
        default: {
          const unhandled: never = outcome;
          console.warn('CheckoutScreen: unhandled outcome', unhandled);
          return;
        }
      }
    },
    [onOutcome, onSucceeded]
  );

  const handleProcessingChange = useCallback((processing: boolean, subtitle?: string) => {
    setPhase(previous =>
      processing
        ? { kind: 'processing', subtitle }
        : previous.kind === 'processing'
          ? { kind: 'selecting' }
          : previous
    );
  }, []);

  if (!purchase) return null;

  if (phase.kind === 'loading') return <SpinLoading wrapperClassName="min-h-[60vh]" />;

  if (phase.kind === 'unavailable') {
    return (
      <ShopCheckoutErrorScreen
        headerTitle={t('App.Checkout.Title')}
        title={t('App.Checkout.PaymentFailedTitle')}
        message={t('App.Checkout.PaymentUnavailable')}
        retryLabel={t('App.Checkout.Retry')}
        onRetry={goBack}
      />
    );
  }

  if (phase.kind === 'failed' || phase.kind === 'pending' || phase.kind === 'refunded') {
    const isPending = phase.kind === 'pending';
    const isRefunded = phase.kind === 'refunded';
    return (
      <ShopCheckoutErrorScreen
        headerTitle={t('App.Checkout.Title')}
        title={
          isRefunded
            ? t('App.Checkout.PaymentRefundedTitle')
            : isPending
              ? t('App.Checkout.PaymentTimedOutTitle')
              : t('App.Checkout.PaymentFailedTitle')
        }
        message={phase.message}
        retryLabel={isPending || isRefunded ? t('App.Labels.GoBack') : t('App.Checkout.Retry')}
        // A pending payment may still land, so retrying it would risk a second charge — leaving is
        // the only safe action. A refunded one would fail identically on retry (the cause, e.g.
        // sold out, hasn't changed), so it leaves too rather than re-offering the same purchase.
        // A failed one is safe to attempt again.
        onRetry={isPending || isRefunded ? goBack : () => setPhase({ kind: 'selecting' })}
      />
    );
  }

  if (!config || !stripePromise) return null;

  return (
    <>
      {/*
        A fixed `height` rather than `minHeight`, so the body below can own the scroll instead of the
        page: `CheckoutForm` is a scrolling region plus a `shrink-0` footer, and `flex-1` only resolves
        inside a bounded parent. With a minimum the footer would be pushed past the viewport and the
        whole page would scroll, which reads as "no scroll" while leaving dead space under the CTA.
        Mirrors `accountPaymentMethodAddApi/index.tsx`. The nav pill is hidden, so only the safe-area
        insets are subtracted.
      */}
      <div
        className="flex flex-col bg-white"
        style={{
          height:
            'calc(100dvh - max(1rem, env(safe-area-inset-top)) - max(1rem, env(safe-area-inset-bottom)))',
        }}
      >
        {/*
        This screen owns its header: `Screen.tsx` returns a mapped `screenContentTypeMap` renderer
        directly and never wraps it in `DefaultScreenLayout`, so nothing else draws one. Back is the
        flow-aware exit rather than `navigate(-1)`, which would land on the checkout again.
      */}
        <ScreenHead
          title={properties?.pageName ?? t('App.Checkout.Title')}
          onBack={goBack}
          ariaLabel={t('App.Labels.GoBack')}
          className="shrink-0 pt-0"
        />

        <Elements key={isMbWay ? 'mbway' : 'card'} stripe={stripePromise} options={elementsOptions}>
          <CheckoutForm
            purchase={purchase}
            config={config}
            savedCards={savedCards}
            cmsPaymentMethods={properties?.paymentMethods}
            locale={locale ?? 'en-GB'}
            termsHref={termsHref}
            selection={selection}
            onSelectionChange={setSelection}
            onProcessingChange={handleProcessingChange}
            onOutcome={handleOutcome}
            resumePaymentId={resumePaymentId}
            formState={formState}
            onFormStateChange={patchFormState}
            onIntentCreated={onIntentCreated}
            stripePromise={stripePromise}
          />
        </Elements>
      </div>

      {/*
        An overlay, never a replacement. Returning `<PaymentProcessing/>` in place of this tree
        unmounted the Payment Element that `confirmPayment` is about to use — Stripe rejected the
        call outright with "elements should have a mounted Payment Element", before anything reached
        the API. The confirm runs against live Elements, so they have to stay in the DOM for the
        whole attempt; the spinner just covers them.
      */}
      {phase.kind === 'processing' && (
        <PaymentProcessing subtitle={phase.subtitle} onLeave={goBack} className="z-100" />
      )}
    </>
  );
}
